
import { useState } from 'react';
import { OnboardingStep } from './useOnboardingState';
import { validateEmail } from '@/utils/email-validator';
import { supabase } from "@/integrations/supabase/client";
import { storeOtp } from '@/utils/otp-utils';
import { useSmsSender } from '@/hooks/use-sms-sender';

// Flag to enable mock verification in development mode
export const ENABLE_MOCK_VERIFICATION = false;

interface OtpSenderProps {
  email: string;
  setExpectedOtp: (otp: string) => void;
  setResendDisabled: (disabled: boolean) => void;
  setCountdown: (count: number) => void;
  setStep: (step: OnboardingStep) => void;
  setIsLoading: (loading: boolean) => void;
  setEmailError: (error: string) => void;
}

export const useOtpSender = ({
  email,
  setExpectedOtp,
  setResendDisabled,
  setCountdown,
  setStep,
  setIsLoading,
  setEmailError
}: OtpSenderProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendSms, isLoading: isSendingSms } = useSmsSender();
  
  // Generate a 6-digit OTP and store it
  const generateAndStoreOtp = () => {
    console.log('=== GENERATING NEW OTP ===');
    
    // Generate a simple 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    
    console.log('Generated OTP:', otp);
    console.log('OTP type:', typeof otp);
    console.log('OTP length:', otp.length);
    
    // Store the OTP with 10 minutes expiration using the correct utility
    storeOtp(otp, 10);
    
    // Also set it in the component state for consistency
    setExpectedOtp(otp);
    
    // Log storage confirmation
    console.log('OTP stored in localStorage with keys: currentOtp, otpExpiry');
    console.log('Stored OTP verification:', localStorage.getItem('currentOtp'));
    console.log('Stored OTP expiry:', localStorage.getItem('otpExpiry'));
    
    return otp;
  };

  // Handle email validation and OTP sending
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("=== STARTING EMAIL CONTINUE PROCESS ===");
    console.log("Validating email:", email);
    
    if (!validateEmail(email)) {
      setEmailError("Veuillez entrer une adresse email valide");
      return;
    }
    
    setIsSubmitting(true);
    setIsLoading(true);
    setEmailError("");
    
    try {
      // Generate and store OTP FIRST
      const otp = generateAndStoreOtp();
      
      console.log("Sending OTP via email:", otp);
      
      // Send the OTP via email using Supabase Edge Function
      const { error } = await supabase.functions.invoke("send-user-otp", {
        body: { email, otp }
      });
      
      if (error) {
        console.error("Error sending OTP via email:", error);
        throw new Error("Erreur lors de l'envoi du code de vérification");
      }
      
      console.log("OTP sent successfully via email");
      console.log(`Code de vérification envoyé à ${email}`);
      
      // Disable resend button for 60 seconds
      setResendDisabled(true);
      setCountdown(60);
      
      // Move to verification step
      setStep('verification');
    } catch (error) {
      console.error("Error in handleEmailContinue:", error);
      setEmailError(error instanceof Error ? error.message : "Une erreur s'est produite");
      alert(error instanceof Error ? error.message : "Une erreur s'est produite");
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Send OTP via SMS to the user's phone
  const sendOtpViaSms = async (phoneNumber: string) => {
    if (!phoneNumber || phoneNumber.length < 8) {
      alert("Numéro de téléphone invalide");
      return false;
    }
    
    try {
      const otp = generateAndStoreOtp();
      console.log(`Sending OTP ${otp} via SMS to ${phoneNumber}`);
      
      const result = await sendSms({
        phoneNumber,
        message: `Votre code de vérification EasyDJ+ est: ${otp}. Ce code expire dans 10 minutes.`,
        tag: 'verification'
      });
      
      if (!result.success) {
        throw new Error("Erreur lors de l'envoi du SMS");
      }
      
      console.log(`Code de vérification envoyé par SMS au ${phoneNumber}`);
      
      // Disable resend button for 60 seconds
      setResendDisabled(true);
      setCountdown(60);
      return true;
    } catch (error) {
      console.error("Error sending OTP via SMS:", error);
      alert(error instanceof Error ? error.message : "Une erreur s'est produite");
      return false;
    }
  };
  
  // Handle resending OTP code
  const handleResendOtp = async () => {
    console.log("=== RESENDING OTP ===");
    console.log("Resending OTP to:", email);
    setIsLoading(true);
    
    try {
      // Generate and store NEW OTP
      const otp = generateAndStoreOtp();
      console.log("Generated new OTP for resend:", otp);
      
      // Send the NEW OTP via email
      const { error } = await supabase.functions.invoke("send-user-otp", {
        body: { email, otp }
      });
      
      if (error) {
        console.error("Error resending OTP:", error);
        throw new Error("Erreur lors du renvoi du code");
      }
      
      console.log("OTP resent successfully:", otp);
      console.log("Nouveau code de vérification envoyé");
      
      // Reset the countdown timer
      setResendDisabled(true);
      setCountdown(60);
    } catch (error) {
      console.error("Error in handleResendOtp:", error);
      alert(error instanceof Error ? error.message : "Une erreur s'est produite");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleEmailContinue,
    handleResendOtp,
    sendOtpViaSms,
    isSubmitting,
    isSendingSms
  };
};
